import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

const OPEN_DELAY_MS = 300
const GAP = 8

type TitledElement = HTMLElement & { titleExpando?: string }

type TooltipState = {
  text: string
  left: number
  top: number
  placement: 'top' | 'bottom'
}

/**
 * Shows every remaining native `title` tooltip as the app's black tooltip.
 *
 * Kendo `<Tooltip>` already turns titles inside it into styled tooltips: on
 * mouseover it moves the title to `titleExpando` and clears `title`. React
 * handles that before this document listener runs, so elements Kendo owns are
 * skipped and only plain `title` attributes (which would show the browser's
 * gray tooltip) are taken over. The attribute is removed while hovering to
 * suppress the native tooltip and restored on mouseout.
 */
export function GlobalTitleTooltip() {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null)
  const activeRef = useRef<{ element: TitledElement; title: string } | null>(
    null
  )
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const clearTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = null
    }

    const release = () => {
      clearTimer()
      const active = activeRef.current
      activeRef.current = null
      setTooltip(null)
      if (active && !active.element.hasAttribute('title')) {
        active.element.setAttribute('title', active.title)
      }
    }

    const handleMouseOver = (event: MouseEvent) => {
      const target = event.target
      if (!(target instanceof Element)) return
      const element = target.closest<TitledElement>('[title]')
      if (activeRef.current && activeRef.current.element === element) return
      if (
        activeRef.current &&
        target instanceof Node &&
        activeRef.current.element.contains(target)
      ) {
        return
      }
      release()
      if (!element || element.titleExpando) return
      const title = element.getAttribute('title')?.trim()
      if (!title) return

      activeRef.current = { element, title }
      element.removeAttribute('title')
      timerRef.current = setTimeout(() => {
        if (activeRef.current?.element !== element || !element.isConnected) {
          return
        }
        const rect = element.getBoundingClientRect()
        const placement = rect.top < 48 ? 'bottom' : 'top'
        setTooltip({
          text: title,
          left: rect.left + rect.width / 2,
          top: placement === 'top' ? rect.top - GAP : rect.bottom + GAP,
          placement,
        })
      }, OPEN_DELAY_MS)
    }

    const handleMouseOut = (event: MouseEvent) => {
      const active = activeRef.current
      if (!active) return
      const next = event.relatedTarget
      if (next instanceof Node && active.element.contains(next)) return
      release()
    }

    // Stay in the bubble phase so Kendo's React handlers can claim titles first.
    document.addEventListener('mouseover', handleMouseOver)
    document.addEventListener('mouseout', handleMouseOut)
    document.addEventListener('mousedown', release, true)
    window.addEventListener('scroll', release, true)
    window.addEventListener('blur', release)
    return () => {
      document.removeEventListener('mouseover', handleMouseOver)
      document.removeEventListener('mouseout', handleMouseOut)
      document.removeEventListener('mousedown', release, true)
      window.removeEventListener('scroll', release, true)
      window.removeEventListener('blur', release)
      release()
    }
  }, [])

  if (!tooltip || typeof document === 'undefined') return null

  return createPortal(
    <span
      role="tooltip"
      className="pointer-events-none fixed z-[1000000] max-w-[264px] whitespace-normal break-words rounded bg-black px-2 py-1 text-sm font-normal leading-5 text-white"
      style={{
        left: tooltip.left,
        top: tooltip.top,
        transform:
          tooltip.placement === 'top'
            ? 'translate(-50%, -100%)'
            : 'translate(-50%, 0)',
      }}
    >
      {tooltip.text}
    </span>,
    document.body
  )
}
